import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AdminsRepository } from "@/api/admins/repository";
import { AdminsService } from "@/api/admins/service";
import { ClientsRepository } from "@/api/clients/repository";
import { ClientsService } from "@/api/clients/service";
import { ContractorsRepository } from "@/api/contractors/repository";
import { ContractorsService } from "@/api/contractors/service";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import { LoanOfficersRepository } from "@/api/loan-officers/repository";
import { LoanOfficersService } from "@/api/loan-officers/service";
import type { IProjectMember } from "@/pages/Projects/types";
import { UserRole } from "@/types/users";
import type { ISelectOption } from "@/ui-kit";
import { Avatar, ComboBox, FormField } from "@/ui-kit";

type IProps = {
  name: string;
  role: UserRole;
  required?: boolean;
};

type IFilters = Partial<{
  searchQuery: string;
  page: string;
  size: string;
  sortField: string;
  sortOrder: string;
  hasCompany: string;
}>;

export const UserControl = ({ name, role, required }: IProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const formValue: IProjectMember[] = useWatch({ name });
  const { errors } = useFormState();

  const defaultOptions = useRef<ISelectOption[] | null>(null);
  const [options, setOptions] = useState<ISelectOption[]>([]);
  // NOTE: Projects support only a single user of each role
  const [localValue, setLocalValue] = useState<ISelectOption | undefined>(
    formValue?.[0]
      ? {
          id: formValue?.[0]?.id,
          text: formValue?.[0]?.fullName,
        }
      : undefined,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const hasMore = useRef<boolean>(true);

  const [filters, setFilters] = useState<IFilters>({
    searchQuery: "",
    page: "1",
    size: "20",
    sortField: "fullName",
    sortOrder: "asc",
    hasCompany: "true",
  });

  useEffect(() => {
    defaultOptions.current = null;
    hasMore.current = true;
  }, [role]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const repository = useMemo(() => {
    const map = {
      [UserRole.Admin]: new AdminsRepository(new AdminsService(), {
        abort: true,
      }),
      [UserRole.Client]: new ClientsRepository(new ClientsService(), {
        abort: true,
      }),
      [UserRole.Contractor]: new ContractorsRepository(
        new ContractorsService(),
        { abort: true },
      ),
      [UserRole.Inspector]: new InspectorsRepository(new InspectorsService(), {
        abort: true,
      }),
      [UserRole.LoanOfficer]: new LoanOfficersRepository(
        new LoanOfficersService(),
        {
          abort: true,
        },
      ),
    };
    return map[role];
  }, [role]);

  const handleSearch = useCallback(
    (filters: IFilters): Promise<ISelectOption[]> => {
      if (!repository) {
        return Promise.resolve([]);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      return repository
        .getAll(filters, {
          signal: abortControllerRef.current.signal,
        })
        .then((response) => {
          if (response.page === response.totalPages) {
            hasMore.current = false;
          }

          return response.content.map((item) => {
            return {
              id: item.id.toString(),
              icon: (
                <Avatar src={item.profilePicturePath} showDetails={false} />
              ),
              text: item.fullName,
              data: item,
            };
          });
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Failed to fetch users:", error);
          }
          throw error;
        })
        .finally(() => {
          if (abortControllerRef.current) {
            abortControllerRef.current = null;
          }
        });
    },
    [repository],
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (
      defaultOptions.current &&
      filters.searchQuery === "" &&
      filters.page === "1"
    ) {
      setOptions(defaultOptions.current);
      return;
    }

    if (filters.page !== "1") {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    handleSearch(filters)
      .then((data) => {
        if (!defaultOptions.current) {
          defaultOptions.current = data;
        }
        if (filters.page !== "1") {
          setLoadingMore(false);
          setOptions((prev) => [...prev, ...data]);
        } else {
          setLoading(false);
          setOptions(data);
        }
      })
      .catch(() => {
        setLoadingMore(false);
        setLoading(false);
      });
  }, [filters, handleSearch]);

  const onSearch = useCallback((query: string) => {
    hasMore.current = true;
    setFilters((prev: IFilters): IFilters => {
      return {
        ...prev,
        searchQuery: query,
        page: "1",
      };
    });
  }, []);

  const onLoad = useCallback(() => {
    if (!hasMore.current || loadingMore) {
      return;
    }
    setFilters((prev: IFilters): IFilters => {
      return {
        ...prev,
        page: `${+(prev.page || "1") + 1}`,
      };
    });
  }, [loadingMore]);

  const onChange = useCallback(
    (option: ISelectOption | ISelectOption[] | undefined) => {
      const o = option as ISelectOption | undefined;
      if (o) {
        setLocalValue(o);
        setValue(name, [
          {
            id: o.id,
            fullName: o.text,
            email: o.data.email,
            role: role,
          },
        ]);
      } else {
        setLocalValue(undefined);
        setValue(name, []);
      }
    },
    [name, setValue, role],
  );

  const onClear = useCallback(() => {
    hasMore.current = true;
    setLocalValue(undefined);
    setValue(name, []);
    setFilters((prev: IFilters): IFilters => {
      return {
        ...prev,
        searchQuery: "",
        page: "1",
      };
    });
  }, [name, setValue]);

  // Full keys per role: composing "Select {{what}}" breaks grammar in other locales
  const text = {
    [UserRole.Admin]: {
      label: t("label.admin"),
      placeholder: t("placeholder.selectAdmin"),
    },
    [UserRole.Contractor]: {
      label: t("label.contractor"),
      placeholder: t("placeholder.selectContractor"),
    },
    [UserRole.Inspector]: {
      label: t("label.inspector"),
      placeholder: t("placeholder.selectInspector"),
    },
    [UserRole.Client]: {
      label: t("label.client"),
      placeholder: t("placeholder.selectClient"),
    },
    [UserRole.LoanOfficer]: {
      label: t("label.loan_officer"),
      placeholder: t("placeholder.selectLoanOfficer"),
    },
    [UserRole.LoanProcessor]: {
      label: t("label.loan_processor"),
      placeholder: t("placeholder.selectLoanProcessor"),
    },
    [UserRole.ProjectManager]: {
      label: t("label.project_manager"),
      placeholder: t("placeholder.selectProjectManager"),
    },
  };

  // The FormField label names the ComboBox input through `<label for>`.
  const inputId = useId();

  return (
    <FormField
      htmlFor={inputId}
      label={text[role].label}
      message={errors?.[name]?.message as string}
      required={required}
    >
      <ComboBox
        inputId={inputId}
        options={options}
        value={localValue}
        onChange={onChange}
        onClear={onClear}
        onSearch={onSearch}
        onLoad={onLoad}
        loading={loading}
        loadingMore={loadingMore}
        placeholder={text[role].placeholder}
        invalid={!!errors?.[name]}
        maxHeight={240}
        openLength={0}
      />
    </FormField>
  );
};
